import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PermissionEnum } from 'src/app/core/enums/permissions.enum';
import { OptionModel } from 'src/app/shared/grid/filters/option.model';
import { SecurityGroupDetail } from '../admin/models';
import { APP_CONFIG, Settings } from '../config/settings';
import { Security } from '../shared/models/security';
import { combine } from '../shared/utilities/route';
import { SecurityGroupLocationProcessResult } from '../admin/models/security-groups-location-process-result.model';

@Injectable({
    providedIn: 'root'
})
export class SecurityService {
    private coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'api', 'securitygroup');
    }

    /* TODO - change this once the endpoint is refactored to return a single DTO object with a list of permissions
     * rather than a list of DTO objects */
    /**
     * @deprecated Please stop using this approach generate boolean flag on the server side instead
     */
    getUserPermissions(): Observable<PermissionEnum[]> {
        return this.http
            .get<Security[]>(combine(this.coreApiPath, 'getsecuritylist'))
            .pipe(map((list) => list.reduce((list, security) => list.concat(security.permissions), [])));
    }

    getUsers(searchText: string, maxItems: number): Observable<OptionModel[]> {
        return this.http.get<OptionModel[]>(
            combine(this.coreApiPath, `users?searchString=${searchText}&take=${maxItems}`)
        );
    }

    getPermissions(): Observable<OptionModel[]> {
        return this.http.get<OptionModel[]>(combine(this.coreApiPath, 'permissions'));
    }

    getPermissionsDetailed(): Observable<OptionModel[]> {
        return this.http.get<any[]>(combine(this.coreApiPath, 'permissions-detailed')).pipe(
            map((results) =>
                results.map((item) => {
                    return {
                        id: item.id,
                        name: item.name,
                        icon: item.isCloneRestricted ? 'block' : null,
                        tooltip: item.isCloneRestricted ? 'Clone Restricted' : null
                    };
                })
            )
        );
    }

    getSecurityGroup(id: number) {
        return this.http.get<any>(`${this.coreApiPath}/${id}`);
    }

    // TODO - remove this function when feature flag faster-user-details is removed
    getSecurityGroupsForUser(userId: string): Observable<SecurityGroupDetail[]> {
        return this.http.post<SecurityGroupDetail[]>(`${this.coreApiPath}/GetSecurityGroupsForUser`, {
            userId: userId
        });
    }

    deleteSecurityGroup(id: number, userId = '') {
        if (!userId) {
            return this.http.delete<any>(`${this.coreApiPath}/delete/${id}`);
        } else {
            return this.http.delete<any>(`${this.coreApiPath}/delete/${id}/${userId}`);
        }
    }

    createSecurityGroup(data: any) {
        return this.http.post<any>(`${this.coreApiPath}/create`, data);
    }

    clonePermissionsToUsers(newUserIds: string[], cloneUserId: string) {
        return this.http.post<any>(`${this.coreApiPath}/clone-all`, {
            newUserIds: newUserIds,
            cloneUserId: cloneUserId
        });
    }

    updateSecurityGroup(data: any) {
        return this.http.patch<any>(`${this.coreApiPath}/update`, data);
    }

    downloadLocationsTemplate(): Observable<HttpResponse<Blob>> {
        const url = combine(this.coreApiPath, 'location-template');
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }

    processLocations(file: File): Observable<SecurityGroupLocationProcessResult[]> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<SecurityGroupLocationProcessResult[]>(combine(this.coreApiPath, 'locations'), formData);
    }
}
